import type { AgentTransportConfig } from '../config/types';
import type { ModelOption } from '@multiplayer-app/ai-agent-types';

const STORAGE_KEY_PREFIX = 'mp-agent-selected-model';

// Special value to represent "Auto" model selection
export const AUTO_MODEL_VALUE = '__auto__';

/**
 * Get storage key for model selection based on transport mode and provider
 */
function getStorageKey(transport: AgentTransportConfig): string {
  if (transport.mode === 'direct') {
    return `${STORAGE_KEY_PREFIX}-${transport.mode}-${transport.provider}`;
  }
  return `${STORAGE_KEY_PREFIX}-${transport.mode}`;
}

/**
 * Save the last selected model to localStorage
 */
export function saveSelectedModel(transport: AgentTransportConfig, modelId: string): void {
  try {
    const key = getStorageKey(transport);
    localStorage.setItem(key, modelId);
  } catch (error) {
    console.warn('Failed to save selected model to localStorage:', error);
  }
}

/**
 * Load the last selected model from localStorage
 */
export function loadSelectedModel(transport: AgentTransportConfig): string | null {
  try {
    const key = getStorageKey(transport);
    return localStorage.getItem(key);
  } catch (error) {
    console.warn('Failed to load selected model from localStorage:', error);
    return null;
  }
}

/**
 * Get available models filtered by transport mode and provider
 */
export function getAvailableModels(
  models: ModelOption[] | undefined,
  transport: AgentTransportConfig
): ModelOption[] {
  if (!models || models.length === 0) {
    return [];
  }

  // Filter models by provider for direct mode
  if (transport.mode === 'direct' && transport.provider !== 'custom') {
    return models.filter((model) => {
      // If model has a provider, it must match
      if (model.provider) {
        return model.provider === transport.provider;
      }
      // If no provider specified, include it (assumes it works with current provider)
      return true;
    });
  }

  // For proxy mode or custom provider, return all models
  return models;
}

/**
 * Get default model based on transport config and available models
 * Returns undefined for "Auto" selection (which uses backend default)
 */
export function getDefaultModel(
  models: ModelOption[] | undefined,
  transport: AgentTransportConfig,
  contextKey?: string
): string | undefined {
  // Try to load from localStorage first (respects user's previous choice)
  const savedModel = loadSelectedModel(transport);
  if (savedModel) {
    // If "auto" is saved, return undefined to use backend default
    if (savedModel === AUTO_MODEL_VALUE) {
      return undefined;
    }
    const availableModels = getAvailableModels(models, transport);
    if (availableModels.some((m) => m.id === savedModel)) {
      return savedModel;
    }
  }

  // If no saved preference, default to "Auto" (undefined) to use backend default
  // This ensures "Auto" is always the first preselected option
  return undefined;
}
